import PropTypes from "prop-types";
import DialogContent from "@material-ui/core/DialogContent";
import makeStyles from "@material-ui/core/styles/makeStyles";
import Grid from "@material-ui/core/Grid";
import React, {useEffect, useState} from "react";
import LegalRichText from "../../view/legal-pages/LegalRichText";
import {Typography, Box, IconButton, Link} from "@material-ui/core";
import DialogActions from "@material-ui/core/DialogActions";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import CustomButton from "../../widget/button/CustomButton";
import CustomCheckbox from "../../widget/checkboxes/CustomCheckbox";
import {useI18N} from "../../../common/common";
import NoIndexDialog from "../noIndex/NoIndexDialog";
import CloseIcon from "../../widget/streamline-icon/CloseIcon";
import {IMPRINT, MARKET_PLACE} from "../../../common/routes";

const useStyles = makeStyles((theme) => ({
    title: {
        padding: 0
    },
    content: {
        paddingRight: "36px",
        paddingLeft: "24px",
        marginTop: theme.spacing(2)
    },
    companyText: {
        marginTop: theme.spacing(5),
    },
    heading: {
        marginBottom: theme.spacing(5),
        wordBreak: "break-word"
    },
    actionsContainer: {
        flexWrap: "nowrap",
        [theme.breakpoints.down("xs")]: {
            fontSize: "14px",
            paddingRight: 0,
            paddingLeft: 0
        },
        paddingRight: theme.spacing(2),
        paddingLeft: theme.spacing(2)
    },
    acceptLabel: {
        [theme.breakpoints.down("xs")]: {
            fontSize: "14px"
        },
    },
    closeButton: {
        position: "absolute",
        right: theme.spacing(0.5),
        top: theme.spacing(0.5),
        color: theme.palette.grey[500],
    },
    mb2: {
        marginBottom: theme.spacing(2),
    },
    contentForNewTerms: {
        padding: theme.spacing(0, 7),
        marginTop: theme.spacing(2),
    },
    actionsContainerForNewTerms: {
        flexWrap: "nowrap",
        padding: theme.spacing(0, 7, 3),
    },
    acceptButton: {
        marginTop: theme.spacing(5),
        marginBottom: theme.spacing(5),
    },
    link: {
        "& a": {
            color: theme.palette.text.primary,
            fontWeight: 700,
            textDecoration: "underline",
        }
    },
}));

const TermsOfUseDialog = ({
    isOpen,
    isMobile,
    onCloseDialog,
    onSubmit,
    disableBackdropClick,
    disableCloseIcon,
    termsOfUse,
    pageTitle,
    companyName,
    placeMonthYear,
    showLatestTermsOfUse,
}) => {

    const classes = useStyles();
    const {i18n} = useI18N();


    const [disabled, setDisabled] = useState(true);
    const [open, setOpen] = React.useState(false);

    useEffect(() => {
        setOpen(isOpen);
    }, [isOpen]);

    const handleClose = () => {
        onCloseDialog();
        setDisabled(true);
    };

    const handleCheckBox = (checked) => {
        setDisabled(!checked);
    };

    const handleSubmit = () => {
        if (onSubmit) {
            onSubmit();
            onCloseDialog();
        }
    };

    const getHyperLink = (str) => {
        return (
            <Link
                key={str}
                href={IMPRINT + MARKET_PLACE + "/"}
                target="_blank"
                rel="noopener noreferrer"
            >
                {str}
            </Link>
        );
    };

    if (termsOfUse?.content && Object.entries(termsOfUse?.content).length !== 0) {
        return (
            <NoIndexDialog
                fullScreen={isMobile}
                fullWidth={!isMobile}
                maxWidth="sm"
                open={open}
                onClose={handleClose}
                disableBackdropClick={disableBackdropClick}
                disableEscapeKeyDown
            >

                <DialogContent className={classes.content}>
                    {!disableCloseIcon &&
                        <Box
                            mb={2}
                        >
                            <IconButton
                                aria-label="close"
                                className={classes.closeButton}
                                onClick={handleClose}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    }
                    <Typography
                        variant={"h1"}
                        className={classes.heading}
                    >
                        {pageTitle}
                    </Typography>

                    {termsOfUse?.content?.map((data, index) => {
                        return (
                            <LegalRichText
                                key={"rich-text-" + index}
                                jsonToConvert={data}
                            />
                        );
                    })
                    }

                    <Typography
                        variant={"body1"}
                        className={classes.companyText}
                    >
                        {companyName}
                    </Typography>

                    <Typography
                        variant={"body1"}
                    >
                        {placeMonthYear}
                    </Typography>

                </DialogContent>
                <DialogActions>
                    <Grid
                        container
                        direction="row"
                        justify="space-between"
                        alignItems="center"
                        spacing={0}
                        className={classes.actionsContainer}
                    >
                        <FormControlLabel
                            control={
                                <CustomCheckbox
                                    color="primary"
                                    onChange={(event) => handleCheckBox(event.target.checked)}
                                />}
                            label={
                                <Typography
                                    className={classes.acceptLabel}
                                >
                                    {i18n("dialog.terms_of_use.terms_of_use_dialog.accept")}
                                </Typography>
                            }
                        />

                        <CustomButton
                            variant={"contained"}
                            size="large"
                            disableElevation
                            disabled={disabled}
                            onClick={() => handleSubmit()}
                        >
                            {i18n("common.label.confirm")}
                        </CustomButton>
                    </Grid>
                </DialogActions>
            </NoIndexDialog>
        );
    }
    else if (showLatestTermsOfUse) {
        return (
            <NoIndexDialog
                fullScreen={isMobile}
                fullWidth={!isMobile}
                maxWidth="sm"
                open={open}
                onClose={handleClose}
                disableBackdropClick={disableBackdropClick}
                disableEscapeKeyDown
            >

                <DialogContent
                    className={classes.contentForNewTerms}
                >
                    {!disableCloseIcon &&
                        <Box
                            mb={2}
                        >
                            <IconButton
                                aria-label="close"
                                className={classes.closeButton}
                                onClick={handleClose}
                            >
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    }

                    <Box
                        display="flex"
                        justifyContent="center"
                        mb={7}
                    >
                        <img
                            src="/images/terms-and-conditions-marketplace.svg"
                            alt={i18n("dialog.terms_of_use.terms_of_use_dialog_new.title")}
                            width={100}
                            height={100}
                        />
                    </Box>

                    <Typography
                        variant={"h1"}
                        align="center"
                        className={classes.mb2}
                    >
                        {i18n("dialog.terms_of_use.terms_of_use_dialog_new.title")}
                    </Typography>

                    <Typography
                        variant={"body1"}
                        align="center"
                        className={classes.link}
                    >
                        {i18n("dialog.terms_of_use.terms_of_use_dialog_new.content", {hyperlink: getHyperLink})}
                    </Typography>
                </DialogContent>
                <DialogActions
                    className={classes.actionsContainerForNewTerms}
                >
                    <Box
                        display="flex"
                        flexDirection="column"
                        justifyContent="center"
                        alignItems="center"
                        mb={2}
                    >
                        <CustomButton
                            variant={"contained"}
                            size="large"
                            disableElevation
                            onClick={() => handleSubmit()}
                            className={classes.acceptButton}
                        >
                            {i18n("dialog.terms_of_use.terms_of_use_dialog_new.accept")}
                        </CustomButton>

                        <Typography
                            variant="caption"
                            align="center"
                            style={{ marginTop: 8 }}
                        >
                            {i18n("dialog.terms_of_use.terms_of_use_dialog_new.caption")}
                        </Typography>
                    </Box>
                </DialogActions>
            </NoIndexDialog>
        );
    }
    else {
        return null;
    }
};

TermsOfUseDialog.defaultProps = {
    isOpen: false,
    isMobile: false,
    disableBackdropClick: true,
    disableCloseIcon: false
};

TermsOfUseDialog.propTypes = {
    isOpen: PropTypes.bool,
    isMobile: PropTypes.bool,
    onCloseDialog: PropTypes.func.isRequired,
    onSubmit: PropTypes.func.isRequired,
    disableBackdropClick: PropTypes.bool,
    disableCloseIcon: PropTypes.bool,
    termsOfUse: PropTypes.object,
    pageTitle: PropTypes.string,
    companyName: PropTypes.string,
    placeMonthYear: PropTypes.string,
    showLatestTermsOfUse: PropTypes.bool,
};

export default TermsOfUseDialog;
